import { PistaLiveIdentityContext, PistaLiveScope } from '../models/moderacion/pista-live.dto';

export function normalizeIdentityText(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\r?\n/g, ' ')
    .replace(/\t/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeIdentitySegment(value: unknown): string {
  const normalized = normalizeIdentityText(value);
  if (!normalized) {
    return '-';
  }

  const slug = normalized
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');

  return slug || '-';
}

function normalizeEspecialIdentitySegment(value: unknown): string {
  const normalized = normalizeIdentityText(value);
  if (!normalized) {
    return '-';
  }

  const tokens = normalized
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((item) => !!item && item !== 'especial');

  if (tokens.length === 0) {
    return '-';
  }

  return Array.from(new Set(tokens)).sort().join('-');
}

export function toPositiveInt(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return Math.trunc(numeric);
}

export function buildEspecialIdentity(context: {
  exposicionId: number;
  especialId?: string | null;
}): string | null {
  const exposicionId = toPositiveInt(context.exposicionId);
  const especialId = normalizeIdentityText(context.especialId);
  if (!exposicionId || !especialId) {
    return null;
  }

  return `especial|expo:${exposicionId}|id:${normalizeEspecialIdentitySegment(especialId)}`;
}

export function buildInscripcionIdentity(context: {
  exposicionId: number;
  inscripcionOrden?: number | null;
  inscripcionSexo?: string | null;
  inscripcionTipo?: string | null;
}): string | null {
  const exposicionId = toPositiveInt(context.exposicionId);
  const orden = toPositiveInt(context.inscripcionOrden);
  if (!exposicionId || !orden) {
    return null;
  }

  const sexo = normalizeIdentitySegment(context.inscripcionSexo);
  const tipo = normalizeIdentitySegment(context.inscripcionTipo);
  return `inscripcion|expo:${exposicionId}|orden:${orden}|sexo:${sexo}|tipo:${tipo}`;
}

export function buildCampeonatoIdentity(context: {
  exposicionId: number;
  inscripcionOrden?: number | null;
  inscripcionSexo?: string | null;
  inscripcionTipo?: string | null;
  campeonatoOrden?: number | null;
  campeonatoNombre?: string | null;
}): string | null {
  const inscripcionIdentity = buildInscripcionIdentity(context);
  if (!inscripcionIdentity) {
    return null;
  }

  const campeonatoOrden = toPositiveInt(context.campeonatoOrden);
  const campeonatoNombre = normalizeIdentityText(context.campeonatoNombre);
  if (!campeonatoOrden && !campeonatoNombre) {
    return null;
  }

  return `${inscripcionIdentity}|campeonato|orden:${campeonatoOrden ?? 0}|nombre:${normalizeIdentitySegment(campeonatoNombre)}`;
}

export function buildCategoriaIdentity(context: {
  exposicionId: number;
  inscripcionOrden?: number | null;
  inscripcionSexo?: string | null;
  inscripcionTipo?: string | null;
  campeonatoOrden?: number | null;
  campeonatoNombre?: string | null;
  categoriaOrden?: number | null;
  categoriaNombre?: string | null;
}): string | null {
  const campeonatoIdentity = buildCampeonatoIdentity(context);
  if (!campeonatoIdentity) {
    return null;
  }

  const categoriaOrden = toPositiveInt(context.categoriaOrden);
  const categoriaNombre = normalizeIdentityText(context.categoriaNombre);
  if (!categoriaOrden && !categoriaNombre) {
    return null;
  }

  return `${campeonatoIdentity}|categoria|orden:${categoriaOrden ?? 0}|nombre:${normalizeIdentitySegment(categoriaNombre)}`;
}

export function buildProductoIdentity(context: {
  exposicionId: number;
  productoId?: number | null;
}): string | null {
  const exposicionId = toPositiveInt(context.exposicionId);
  const productoId = toPositiveInt(context.productoId);
  if (!exposicionId || !productoId) {
    return null;
  }

  return `producto|expo:${exposicionId}|id:${productoId}`;
}

export function buildScopeIdentity(
  scope: PistaLiveScope,
  context: PistaLiveIdentityContext
): string | null {
  if (scope === 'especial') {
    return buildEspecialIdentity(context);
  }
  if (scope === 'inscripcion') {
    return buildInscripcionIdentity(context);
  }
  if (scope === 'campeonato') {
    return buildCampeonatoIdentity(context);
  }
  if (scope === 'categoria') {
    return buildCategoriaIdentity(context);
  }
  return buildProductoIdentity({
    exposicionId: context.exposicionId,
    productoId: context.productoId
  });
}
